<script setup lang="ts">
import {
  IForm,
  IFormError,
  IFormGroup,
  IFormLabel,
  IInput,
  useForm,
} from '@inkline/inkline'
import FormConsent from './FormConsent.vue'
import Icon from '~/components/global/Icon.vue'
import { useAPIRequest } from '~/composables/api/useAPIRequest'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import trackGtmEvent from '~/utils/tracking/tackGtmEvent'
import getFormTrackingData from '~/utils/tracking/getFormTrackingData'
import type useArrangeVisitModalStore from '~/stores/modals/useArrangeVisitModal'

const props = defineProps<{
  modalStore: ReturnType<typeof useArrangeVisitModalStore>
  estateData: EstateDetailInterface
}>()

const { executeRecaptcha } = useGoogleRecaptcha()
const { t } = useI18n()
const { form, schema, validate } = useForm({
  name: {
    validators: [
      {
        name: 'required',
        message: t('forms.arrangeVisit.fields.name.requiredMessage'),
      },
    ],
  },
  phone: {
    validators: [
      {
        name: 'required',
        message: t('forms.arrangeVisit.fields.phone.requiredMessage'),
      },
      {
        name: 'custom',
        message: t('forms.arrangeVisit.fields.phone.invalidMessage'),
        validator: (v: string) => !v || /^\+?[0-9 ]+$/.test(v),
      },
    ],
  },
  email: {
    validators: [
      {
        name: 'required',
        message: t('forms.arrangeVisit.fields.email.requiredMessage'),
      },
      {
        name: 'email',
        message: t('forms.arrangeVisit.fields.email.invalidMessage'),
      },
    ],
  },
})

function setValidatorToShowErrors() {
  let isFirst = true
  for (const schemaFieldKey of Object.keys(schema.value)) {
    const schemaField = schema.value[schemaFieldKey]

    if (schemaField?.constructor !== Object || schemaField?.valid) {
      continue
    }

    if (isFirst) {
      ;(
        document.querySelector(`[name="${schemaFieldKey}"]`) as HTMLInputElement
      )?.focus()

      isFirst = false
    }

    schemaField.value = schemaField.value ?? ''
    schemaField.pristine = false
    schemaField.dirty = true
  }
}

function resetForm() {
  for (const schemaFieldKey of Object.keys(schema.value)) {
    const schemaField = schema.value[schemaFieldKey]

    if (schemaField?.constructor !== Object) {
      continue
    }

    delete schemaField.value
    schemaField.pristine = true
    schemaField.dirty = false
  }
}

const isSubmitting = ref(false)
async function onSubmit() {
  isSubmitting.value = true

  await validate()

  if (!schema.value.valid) {
    setValidatorToShowErrors()

    isSubmitting.value = false

    return false
  }

  const serviceLines = {
    office: 'Office Agency',
    warehouse: 'Industrial Agency',
    retail: 'Retail Agency',
  }

  const { token } = await executeRecaptcha('arrange_visit')

  useAPIRequest('/contact/send', {
    method: 'POST',
    body: {
      ...form.value,
      service_line: serviceLines[props.estateData.type],
      type: 'Property detail - Arrange Visit',
      url: window.location.origin + window.location.pathname,
      token,
    },
  })
    .then((response) => {
      if (!response.status) {
        showToast({
          title: t('forms.arrangeVisit.errorTitle'),
          message: response.error || response.message,
          color: 'danger',
        })

        return
      }

      showToast({
        title: t('forms.arrangeVisit.successTitle'),
        message: t('forms.arrangeVisit.successMessage'),
      })

      trackGtmEvent('odoslanie_formularu', {
        form_type: 'dohodnut-obhliadku',
        ...getFormTrackingData(),
      })

      resetForm()
      props.modalStore.close()
    })
    .catch((error) => {
      showToast({
        title: t('forms.arrangeVisit.errorTitle'),
        message: error.message,
        color: 'danger',
      })
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

watch(
  () => props.modalStore.isOpened,
  (isOpened) => {
    if (!isOpened) return

    trackGtmEvent('videnie_formularu', {
      form_type: 'dohodnut-obhliadku',
      ...getFormTrackingData(),
    })
  }
)
</script>

<template>
  <IForm
    v-model="schema"
    novalidate
    class="c-tailored-offer-form has-floating-labels"
    @submit="onSubmit"
  >
    <IFormGroup>
      <IFormLabel>
        <span class="sc-label"
          >{{ $t('forms.arrangeVisit.fields.name.label') }}
          <span class="sc-required">*</span>
        </span>
      </IFormLabel>
      <IInput name="name" placeholder="" />
      <IFormError for="name" />
    </IFormGroup>

    <IFormGroup>
      <IFormLabel>
        <span class="sc-label"
          >{{ $t('forms.arrangeVisit.fields.email.label') }}
          <span class="sc-required">*</span>
        </span>
      </IFormLabel>
      <IInput name="email" type="email" placeholder="" />
      <IFormError for="email" />
    </IFormGroup>

    <IFormGroup>
      <IFormLabel>
        <span class="sc-label"
          >{{ $t('forms.arrangeVisit.fields.phone.label') }}
          <span class="sc-required">*</span>
        </span>
      </IFormLabel>
      <IInput name="phone" type="tel" placeholder="" />
      <IFormError for="phone" />
    </IFormGroup>

    <IFormGroup>
      <IButton
        class="has-icon"
        color="secondary"
        type="submit"
        size="lg"
        block
        :loading="isSubmitting"
        :show-loading-icon="true"
        @click="onSubmit"
      >
        {{ $t('forms.arrangeVisit.submit') }}

        <Icon name="arrow-right-thick" />
      </IButton>
    </IFormGroup>

    <IFormGroup>
      <FormConsent class="c-tailored-offer-form__consent" />
    </IFormGroup>
  </IForm>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-tailored-offer-form {
  &__consent {
    font-size: 15px;
    line-height: 1.28;
    font-weight: 400;
    color: var(--color-text-dark-2);
  }
}
</style>
